import { useMemo } from 'react';
import { useSelector } from 'react-redux';
import { RootState } from 'src/redux/store';

export interface UseIsConfiguredToOtherSiteResult {
  isConfiguredToOtherSite: boolean;
  siteURL?: string;
  configuredSiteId?: string;
  isSiteConfiguredToOtherGbp?: boolean;
  configuredGbpName?: string;
}

/**
 * Generic custom hook to check if an agent or feature is configured for a different site
 * than the currently active selected site.
 *
 * @param agentSiteId - The site ID configured for the agent (e.g. userAgentDetails?.response?.local_seo?.site_id)
 * @param siteIdOverride - Optional override for current site ID (defaults to redux active site)
 * @returns { isConfiguredToOtherSite: boolean, siteURL?: string, configuredSiteId?: string }
 */
export const useIsConfiguredToOtherSite = (
  agentSiteId?: string,
  siteIdOverride?: string
): UseIsConfiguredToOtherSiteResult => {
  const { _id: reduxSiteId, url: reduxSiteName } = useSelector(
    (state: RootState) => state?.siteAudit?.siteAuditDetails
  ) ?? {};

  const auditSiteDetails = useSelector(
    (state: RootState) => state?.siteAudit?.auditSiteDetails
  ) ?? [];

  const currentSiteId = siteIdOverride || reduxSiteId;

  return useMemo(() => {
    if (!agentSiteId || !currentSiteId) {
      return {
        isConfiguredToOtherSite: false,
        siteURL: reduxSiteName,
        configuredSiteId: agentSiteId
      };
    }

    const isConfiguredToOther = String(agentSiteId) !== String(currentSiteId);

    let resolvedSiteURL = reduxSiteName;
    if (isConfiguredToOther && Array.isArray(auditSiteDetails) && auditSiteDetails.length > 0) {
      const matchedSite = auditSiteDetails.find(
        (site: any) =>
          String(site?._id) === String(agentSiteId) ||
          String(site?.site_id) === String(agentSiteId)
      );
      if (matchedSite) {
        resolvedSiteURL = matchedSite?.url || matchedSite?.site_name || matchedSite?.name || '';
      }
    }

    return {
      isConfiguredToOtherSite: isConfiguredToOther,
      siteURL: resolvedSiteURL,
      configuredSiteId: agentSiteId
    };
  }, [agentSiteId, currentSiteId, reduxSiteName, auditSiteDetails]);
};

const extractCleanId = (val: any): string => {
  if (val === null || val === undefined) return '';
  const str = String(val).trim();
  if (str.includes('/')) {
    return str.split('/').pop() || '';
  }
  return str;
};

export const isMatchingGbpLocation = (
  loc: any,
  targetLocId?: string | number,
  targetAccId?: string | number
): boolean => {
  if (!loc || (!targetLocId && !targetAccId)) return false;

  const cleanTargetLocId = extractCleanId(targetLocId);
  const cleanTargetAccId = extractCleanId(targetAccId);

  const locLocId = extractCleanId(
    loc?.locationId || loc?.location_id || loc?.name || loc?.id || loc?._id
  );
  const locAccId = extractCleanId(
    loc?.accountId || loc?.account_id
  );

  const rawLocName = String(loc?.name || '').trim();
  const rawLocTitle = String(loc?.title || loc?.locationName || '').trim();

  const matchesLoc = cleanTargetLocId
    ? locLocId === cleanTargetLocId ||
      rawLocName === String(targetLocId) ||
      rawLocName === `locations/${cleanTargetLocId}` ||
      rawLocTitle === String(targetLocId)
    : false;

  const matchesAcc = cleanTargetAccId
    ? locAccId === cleanTargetAccId ||
      String(loc?.accountId || loc?.account_id) === String(targetAccId) ||
      String(loc?.accountId || loc?.account_id) === `accounts/${cleanTargetAccId}`
    : false;

  // If both target location and account IDs are specified
  if (cleanTargetLocId && cleanTargetAccId) {
    if (locAccId) {
      return matchesLoc && matchesAcc;
    }
    return matchesLoc;
  }

  if (cleanTargetLocId) {
    return matchesLoc;
  }

  return matchesAcc;
};

export const getProfileDisabledState = (
  profile: any,
  localSeoApiData?: { site_id?: string; location_id?: string; account_id?: string } | any,
  currentSiteId?: string,
  otherSiteURL?: string,
  auditSiteDetails?: any[],
  reviewAutomationApiData?: { site_id?: string; location_id?: string; account_id?: string } | any
): { disabled: boolean; tooltip: string } => {
  if (!profile) {
    return { disabled: false, tooltip: '' };
  }

  const resolveSiteName = (targetSiteId?: string): string => {
    if (!targetSiteId) return otherSiteURL || '';
    if (Array.isArray(auditSiteDetails) && auditSiteDetails.length > 0) {
      const matched = auditSiteDetails.find(
        (s: any) => String(s?._id) === String(targetSiteId) || String(s?.site_id) === String(targetSiteId)
      );
      if (matched) {
        return matched?.url || matched?.site_name || matched?.name || '';
      }
    }
    return otherSiteURL || '';
  };

  // 1. Check if the profile directly carries a different site_id / connectedSiteId / squareSpaceSiteId
  const profileSiteId = profile?.site_id || profile?.connectedSiteId || profile?.squareSpaceSiteId;
  if (profileSiteId && currentSiteId && String(profileSiteId) !== String(currentSiteId)) {
    const siteName = resolveSiteName(profileSiteId);
    return {
      disabled: true,
      tooltip: siteName
        ? `Already configured with another site: ${siteName}`
        : 'Already configured with another site'
    };
  }

  // 2. Normalize localSeoApiData and reviewAutomationApiData into a combined array of items
  const localSeoItems: Array<any> = Array.isArray(localSeoApiData)
    ? localSeoApiData
    : (localSeoApiData ? [localSeoApiData] : []);
  const reviewAutomationItems: Array<any> = Array.isArray(reviewAutomationApiData)
    ? reviewAutomationApiData
    : (reviewAutomationApiData ? [reviewAutomationApiData] : []);
  const agentItems: Array<any> = [...localSeoItems, ...reviewAutomationItems];

  for (const item of agentItems) {
    if (!item) continue;
    const boundSiteId = item?.site_id;
    const boundLocId = item?.location_id;
    const boundAccId = item?.account_id;

    if (!boundLocId && !boundAccId) continue;

    // Case 1: The profile is configured to another site
    if (boundSiteId && currentSiteId && String(boundSiteId) !== String(currentSiteId)) {
      if (isMatchingGbpLocation(profile, boundLocId, boundAccId)) {
        const siteName = resolveSiteName(boundSiteId);
        return {
          disabled: true,
          tooltip: siteName
            ? `Already configured with another site: ${siteName}`
            : 'Already configured with another site'
        };
      }
    }

    // Case 2: The current site is already configured/bound to a GBP (even if disabled), and this profile is NOT that GBP
    if (boundSiteId && currentSiteId && String(boundSiteId) === String(currentSiteId)) {
      if (!isMatchingGbpLocation(profile, boundLocId, boundAccId)) {
        return {
          disabled: true,
          tooltip: 'This site is already configured with another Google Business Profile'
        };
      }
    }
  }

  return { disabled: false, tooltip: '' };
};

export default useIsConfiguredToOtherSite;


